import { gql, useQuery } from "@apollo/client";
import { Dialog, Icon, Link, ListItem, OptionSeparator } from "@resource/atlas";
import { atlasWarning } from "@resource/atlas/icons";
import { strings } from "@resource/common";
import {
  JobRoleGuideTemplateSetupStatus,
  UnconfiguredJobRoleGuideTemplates,
} from "generated/schemaTypes";
import { analytics } from "react-hooks/useAnalytics";
import { formatEntity } from "shared/constants/entities";

const UNCONFIGURED_JOB_ROLE_GUIDE_TEMPLATES = gql`
  query UnconfiguredJobRoleGuideTemplates {
    currentOrganization {
      id
      unconfiguredJobRoleGuideTemplates {
        id
        jobRoleName
        unconfiguredStageCount
        setupStatus
      }
    }
  }
`;

function UnconfiguredJobsBanner() {
  const { data: unconfiguredData } =
    useQuery<UnconfiguredJobRoleGuideTemplates>(
      UNCONFIGURED_JOB_ROLE_GUIDE_TEMPLATES
    );

  const jrgts =
    unconfiguredData?.currentOrganization?.unconfiguredJobRoleGuideTemplates.filter(
      (jrgt) =>
        jrgt.setupStatus === JobRoleGuideTemplateSetupStatus.SETUP_COMPLETE
    ) || [];

  if (jrgts.length === 0) {
    return null;
  }

  return (
    <div className="flex justify-between items-center bg-danger-50 py-3 px-4 rounded-lg mt-4">
      <div className="flex text-danger-900 text-body-md">
        <Icon className="mr-3 text-danger-500" content={atlasWarning} />
        <div>
          You have {strings.pluralize("job", jrgts.length)} with{" "}
          {jrgts.length === 1 ? "a " : ""}missing or incomplete{" "}
          {formatEntity("interview plan")}
          {jrgts.length > 1 ? "s" : ""}.
        </div>
      </div>
      <Dialog.Root>
        <Dialog.Trigger>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            as="button"
            variant="danger"
            className="text-body-md"
            onClick={() =>
              analytics.track("Unconfigured Jobs Banner View Jobs Clicked")
            }
          >
            View jobs
          </Link>
        </Dialog.Trigger>
        <Dialog.Content
          hasPadding={false}
          size="small"
          header={{
            title: `Missing or incomplete ${formatEntity("interview plan", {
              plural: true,
            })}`,
          }}
        >
          <div className="px-3 pb-6">
            {jrgts.map((jrgt, index) => (
              <div key={jrgt.id}>
                <a
                  href={`/jobs/${jrgt.id}/stages`}
                  target="_blank"
                  rel="noreferrer"
                  onClickCapture={() =>
                    analytics.track("Unconfigured Jobs Dialog Job Clicked")
                  }
                >
                  <ListItem
                    isInteractive
                    renderContent={({ children }) => (
                      <div className="flex justify-between items-center">
                        <div className="text-body-md">{children}</div>
                        <div className="text-body-sm text-subtle shrink-0">
                          {jrgt.unconfiguredStageCount
                            ? strings.pluralize(
                                "stage issue",
                                jrgt.unconfiguredStageCount
                              )
                            : "No stages"}
                        </div>
                      </div>
                    )}
                  >
                    {jrgt.jobRoleName}
                  </ListItem>
                </a>
                {index < jrgts.length - 1 && (
                  <OptionSeparator className="my-0 mx-3 mw-0" />
                )}
              </div>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}

export default UnconfiguredJobsBanner;
